import { linkTypeFromQuery, utmFromQuery, utmFromReferrer, utmOf, type OriginReport, type Utm } from "@fusiqo/vozqi-shared";

/**
 * WHERE THIS VISIT CAME FROM (spec «Embudo para técnicos» §2.2): the campaign
 * tags of the link that brought somebody here, kept for the first sign-in to
 * hand to the server, and the trade an ad's link names, kept for the first
 * business's wizard. The shape of affiliate.ts: nothing here talks to anybody
 * but this app's API, and nothing here may break a screen.
 *
 * `localStorage` for the tags, for ORIGIN_MAX_AGE_DAYS: somebody who reads
 * /tecnicos today and signs up next week from the same browser is the case
 * this exists for. THE FIRST LINK WINS while it lasts, as on the server.
 * `sessionStorage` for the trade: it only matters to the sign-up this visit
 * leads to.
 */
const ORIGIN_KEY = "vozqi_origin";
const LINK_TYPE_KEY = "vozqi_link_type";

/** How long a seen link counts, in days. */
const ORIGIN_MAX_AGE_DAYS = 90;

type StoredOrigin = { utm: Utm; seenAt: string };

function readStoredOrigin(now: Date): StoredOrigin | null {
  try {
    const raw = localStorage.getItem(ORIGIN_KEY);
    if (raw === null) return null;
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== "object" || parsed === null) return null;
    const { utm, seenAt } = parsed as { utm?: unknown; seenAt?: unknown };
    if (typeof seenAt !== "string" || typeof utm !== "object" || utm === null) return null;
    const seen = new Date(seenAt).getTime();
    if (Number.isNaN(seen)) return null;
    if ((now.getTime() - seen) / 86_400_000 > ORIGIN_MAX_AGE_DAYS) {
      // Past its days the link counts for nothing, and the privacy policy
      // says it is not kept: it goes.
      localStorage.removeItem(ORIGIN_KEY);
      return null;
    }
    const clean = utmOf(utm as Partial<Utm>);
    return clean === null ? null : { utm: clean, seenAt };
  } catch {
    // Unreadable or tampered: as if there were none.
    return null;
  }
}

/** An origin kept from a plain visit: no campaign, only the site it came from. */
const isReferrerOnly = (utm: Utm): boolean =>
  utm.campaign === null && (utm.medium === "referral" || utm.medium === "organic");

/**
 * Called once from main.tsx before React mounts, so the link is read before
 * the router can rewrite the URL. The tags come from the link, or, when it
 * has none, from the referrer (just its host). Synchronous, local, and it never throws.
 */
export function captureOrigin(): void {
  const search = window.location.search;
  try {
    const linkType = linkTypeFromQuery(search);
    if (linkType !== null) sessionStorage.setItem(LINK_TYPE_KEY, linkType);
  } catch {
    // Private browsing: the wizard simply starts with nothing chosen.
  }
  try {
    const now = new Date();
    const stored = readStoredOrigin(now);
    // A link's own tags win over a kept origin that only came from a referrer;
    // between two links the first wins, and a referrer never replaces anything.
    const utm = utmFromQuery(search) ?? (stored === null ? utmFromReferrer(document.referrer, window.location.hostname) : null);
    if (utm === null || (stored !== null && !isReferrerOnly(stored.utm))) return;
    localStorage.setItem(ORIGIN_KEY, JSON.stringify({ utm, seenAt: now.toISOString() }));
  } catch {
    // Storage refused: the visit simply is not attributed.
  }
}

/**
 * Hands the kept tags to the server once the session is real, and forgets
 * them once it answered. Swallows everything: the server keeps the first
 * campaign and writes nothing for an account older than the link.
 */
export async function sendOrigin(report: (input: OriginReport) => Promise<void>): Promise<void> {
  const stored = readStoredOrigin(new Date());
  if (stored === null) return;
  try {
    await report({ channel: "web", utm: stored.utm, seenAt: stored.seenAt });
    localStorage.removeItem(ORIGIN_KEY);
  } catch {
    // Offline, or a token about to refresh: the next sign-in tries again.
  }
}

/** The trade an ad's link named during this visit, as its slug, or null. */
export function readLinkType(): string | null {
  try {
    return sessionStorage.getItem(LINK_TYPE_KEY);
  } catch {
    return null;
  }
}

/** Spent once the first business is born: a second one is never preselected. */
export function clearLinkType(): void {
  try {
    sessionStorage.removeItem(LINK_TYPE_KEY);
  } catch {
    // Nothing to undo if storage cannot be touched.
  }
}
